import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Clock, CheckCircle2, Users, Loader2, Info, Link2, Copy, Check, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import DashLayout from "@/components/xuma/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const commTypeLabel = (t) => (t === "config" ? "Configuración (75%)" : "Mensual (30%)");
const PLAN_LABELS = {
  basic: "Básico ($699 / $99)", premium: "Premium ($1,499 / $299)",
  founder_basic: "Fundador Básico ($499 / $59)", founder_premium: "Fundador Premium ($999 / $199)",
};

export default function DistributorPanel() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saleOpen, setSaleOpen] = useState(false);

  const load = () => api.get("/distributor/summary").then(({ data }) => setData(data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  if (!data) {
    return <DashLayout title="Mis comisiones"><div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-xuma-primary" /></div></DashLayout>;
  }

  const { totals, sales, commissions, ref_code } = data;
  const refUrl = `${window.location.origin}/r/${ref_code}`;
  const copy = async () => { await navigator.clipboard.writeText(refUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <DashLayout title="Mis comisiones">
      {/* Referral link */}
      <div className="rounded-2xl bg-xuma-dark text-white p-5 mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-heading font-bold flex items-center gap-2"><Link2 className="h-4 w-4 text-xuma-accent" /> Tu enlace de referido</p>
            <p className="text-xs text-white/60 mt-1">Compártelo para registrar ventas sin depender del admin.</p>
          </div>
          <Button data-testid="register-sale-btn" onClick={() => setSaleOpen(true)} className="bg-xuma-primary hover:bg-xuma-primaryHover text-white rounded-full gap-1.5">
            <Plus className="h-4 w-4" /> Registrar venta
          </Button>
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-xl bg-white/10 border border-white/10 p-2">
          <span className="text-sm text-white/80 truncate flex-1 pl-2" data-testid="ref-url">{refUrl}</span>
          <Button size="sm" variant="ghost" onClick={copy} data-testid="copy-ref-btn" className="text-white hover:bg-white/10 gap-1">
            {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />} {copied ? "Copiado" : "Copiar"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl bg-xuma-primary/5 border border-xuma-primary/20 p-4 mb-6 flex items-start gap-3 text-sm">
        <Info className="h-4 w-4 text-xuma-primary mt-0.5 shrink-0" />
        <p className="text-xuma-dark">Ganas el <b>75%</b> de la cuota de configuración de cada tarjeta vendida y el <b>30%</b> de la mensualidad mientras el cliente siga activo.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={Clock} label="Por cobrar" value={money(totals.pending)} accent />
        <Kpi icon={CheckCircle2} label="Pagado" value={money(totals.paid)} />
        <Kpi icon={Users} label="Clientes" value={totals.clients} />
        <Kpi icon={Wallet} label="Activos" value={totals.active_clients} />
      </div>

      <h2 className="font-heading font-bold text-lg text-xuma-dark mb-3">Mis clientes</h2>
      <div className="rounded-2xl bg-white border border-xuma-border overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-xuma-border text-left text-xs uppercase tracking-wider text-xuma-muted">
              {["Cliente", "Plan", "Config", "Mensual", "Estado"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
            </tr></thead>
            <tbody>
              {sales.map((s) => (
                <tr key={s.sale_id} className="border-b border-xuma-border last:border-0">
                  <td className="px-4 py-3 font-medium text-xuma-dark">{s.client_name}</td>
                  <td className="px-4 py-3 text-xuma-muted">{s.plan_label}</td>
                  <td className="px-4 py-3">{money(s.config_fee)}</td>
                  <td className="px-4 py-3">{money(s.monthly_fee)}</td>
                  <td className="px-4 py-3"><span className={`text-xs rounded-full px-3 py-1 ${s.status === "active" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>{s.status === "active" ? "Activo" : "Cancelado"}</span></td>
                </tr>
              ))}
              {sales.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-xuma-muted">Aún no tienes clientes asignados.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <h2 className="font-heading font-bold text-lg text-xuma-dark mb-3">Detalle de comisiones</h2>
      <div className="rounded-2xl bg-white border border-xuma-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-xuma-border text-left text-xs uppercase tracking-wider text-xuma-muted">
              {["Cliente", "Tipo", "Periodo", "Monto", "Estado"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
            </tr></thead>
            <tbody>
              {commissions.map((c) => (
                <tr key={c.commission_id} className="border-b border-xuma-border last:border-0">
                  <td className="px-4 py-3 font-medium text-xuma-dark">{c.client_name}</td>
                  <td className="px-4 py-3 text-xuma-muted">{commTypeLabel(c.type)}</td>
                  <td className="px-4 py-3 text-xuma-muted">{c.period}</td>
                  <td className="px-4 py-3 font-medium">{money(c.amount)}</td>
                  <td className="px-4 py-3"><span className={`text-xs rounded-full px-3 py-1 ${c.status === "paid" ? "bg-green-50 text-green-700" : "bg-xuma-accent/20 text-xuma-dark"}`}>{c.status === "paid" ? "Pagada" : "Pendiente"}</span></td>
                </tr>
              ))}
              {commissions.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-xuma-muted">Aún no hay comisiones.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <RegisterSaleDialog open={saleOpen} onOpenChange={setSaleOpen} refCode={ref_code} onCreated={load} />
    </DashLayout>
  );
}

function RegisterSaleDialog({ open, onOpenChange, refCode, onCreated }) {
  const [form, setForm] = useState({ client_name: "", client_email: "", plan: "basic" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/ref/${refCode}/sale`, form);
      toast.success("Venta registrada");
      onOpenChange(false); setForm({ client_name: "", client_email: "", plan: "basic" });
      onCreated();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Registrar venta</DialogTitle>
          <DialogDescription>La comisión de configuración (75%) se genera automáticamente.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="mb-1 block">Nombre del cliente</Label><Input data-testid="dsale-client" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
          <div><Label className="mb-1 block">Correo del cliente (opcional)</Label><Input data-testid="dsale-email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} /></div>
          <div><Label className="mb-1 block">Plan</Label>
            <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
              <SelectTrigger data-testid="dsale-plan"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(PLAN_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="dsale-submit" onClick={submit} disabled={saving || !form.client_name} className="bg-xuma-primary hover:bg-xuma-primaryHover text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar venta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Kpi = ({ icon: Icon, label, value, accent }) => (
  <div className={`rounded-2xl border p-5 ${accent ? "bg-xuma-primary text-white border-xuma-primary" : "bg-white border-xuma-border"}`}>
    <Icon className={`h-5 w-5 ${accent ? "text-white" : "text-xuma-primary"}`} />
    <p className="mt-3 font-heading font-extrabold text-2xl">{value}</p>
    <p className={`text-sm ${accent ? "text-white/80" : "text-xuma-muted"}`}>{label}</p>
  </div>
);
